import { useState } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import {
  CircularProgress,
  Popover,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import SearchBar from 'material-ui-search-bar'
import { COLORS } from 'src/utils/Themes/colors'
import polygon_47 from 'src/assets/Button_images/polygon_47.svg'
import { getClientId } from 'src/utils/commonMethods'
import { GET_CORPORATE_EMPLOYEE_DETAILS } from 'src/services/individualService'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import RegistrationStatsSearch from 'src/components/ceo/RegistrationStatsSearch'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { EMPLOYEE_STATS_USER_TYPES } from 'src/utils/constants/common'
import {
  ACTIVE_EMPLOYEES,
  ACTIVE_LOGGED_IN,
  ACTIVE_NOT_LOGGED_IN,
} from 'src/utils/constants/Ceo'

const EmployeeType = ({ clientId }) => {
  const classes = useStyles()
  const userInfo = useSelector((state) => state?.userInfo)
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const [searchedTemp, setSearchedTemp] = useState('')
  const [selectedUserType, setSelectedUserType] = useState('')

  const {
    loading: userLoggedInLoading,
    error: userLoggedInError,
    data: userLoggedInData,
  } = useQuery(GET_CORPORATE_EMPLOYEE_DETAILS(), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    variables: {
      clientId: getClientId(clientId, userInfo),
      userType: EMPLOYEE_STATS_USER_TYPES?.LOGGED_IN,
      search: '',
    },
  })

  const { loading: userNotLoggedInLoading, data: userNotLoggedInData } =
    useQuery(GET_CORPORATE_EMPLOYEE_DETAILS(), {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      variables: {
        clientId: getClientId(clientId, userInfo),
        userType: EMPLOYEE_STATS_USER_TYPES?.NON_LOGGED,
        search: '',
      },
    })

  const { loading: userRegisteredLoading, data: userRegisteredData } = useQuery(
    GET_CORPORATE_EMPLOYEE_DETAILS(),
    {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      variables: {
        clientId: getClientId(clientId, userInfo),
        userType: EMPLOYEE_STATS_USER_TYPES?.REGISTERED,
        search: '',
      },
    }
  )

  if (userLoggedInLoading || userNotLoggedInLoading || userRegisteredLoading)
    return (
      <div className={classes.circularLoader}>
        <CircularProgress />
      </div>
    )
  if (userLoggedInError) {
    return (
      <div>
        <GraphQlErrors error={userLoggedInError} />
      </div>
    )
  }

  const handleClick = (event, userType) => {
    setAnchorEl(event?.currentTarget)
    setSelectedUserType(userType)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSearchedTemp('')
  }
  return (
    <div className={classes.root}>
      <div className='parentEmployeeType'>
        <Popover
          id={'popover'}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography className={classes.typographyStyles}>
            <div className={classes.searchBarContainer}>
              <SearchBar
                id='searchBar'
                className={classes.searchBar}
                value={searchedTemp}
                autoFocus={true}
                placeholder={'Emp ID/ Name'}
                onCancelSearch={() => setSearchedTemp('')}
                onChange={(newValue) => {
                  setSearchedTemp(newValue)
                }}
              />
            </div>
            <RegistrationStatsSearch
              userType={selectedUserType}
              searchText={searchedTemp}
              clientId={getClientId(clientId, userInfo)}
            />
          </Typography>
        </Popover>
        <Grid container spacing={4} className={classes.gridContainer}>
          <Grid item className={classes.employeeItem}>
            <div className={classes.info}>
              <Tooltip title={ACTIVE_EMPLOYEES}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </div>
            <item className={classes.employeeType}>
              <p>Registered Employees</p>
            </item>
            <item className={classes.countItem}>
              <p className={classes.employeeCount}>
                {userRegisteredData?.getUserDetails?.count ?? 0}
              </p>
              <button
                onClick={(event) =>
                  handleClick(event, EMPLOYEE_STATS_USER_TYPES?.REGISTERED)
                }
                className={classes.arrowDownButton}
                id='registeredEmployeesButton'
              >
                <img className={classes.buttonImage} src={polygon_47} alt='' />
              </button>
            </item>
          </Grid>
          <Grid item className={classes.employeeItem}>
            <div className={classes.info}>
              <Tooltip title={ACTIVE_LOGGED_IN}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </div>
            <item className={classes.employeeType}>
              <p>Logged-in Employees </p>
            </item>
            <item className={classes.countItem}>
              <p className={classes.employeeCount}>
                {userLoggedInData?.getUserDetails?.count ?? 0}
              </p>
              <button
                onClick={(event) =>
                  handleClick(event, EMPLOYEE_STATS_USER_TYPES?.LOGGED_IN)
                }
                className={classes.arrowDownButton}
                id='loggedInEmployeesButton'
              >
                <img className={classes.buttonImage} src={polygon_47} alt='' />
              </button>
            </item>
          </Grid>
          <Grid item className={classes.employeeItem}>
            <div className={classes.info}>
              <Tooltip title={ACTIVE_NOT_LOGGED_IN}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </div>
            <item className={classes.employeeType}>
              <p>Employees didn&apos;t log in</p>
            </item>
            <item className={classes.countItem}>
              <p className={classes.employeeCount}>
                {userNotLoggedInData?.getUserDetails?.count ?? 0}
              </p>
              <button
                onClick={(event) =>
                  handleClick(event, EMPLOYEE_STATS_USER_TYPES?.NON_LOGGED)
                }
                className={classes.arrowDownButton}
                id='loggedOutEmployeesButton'
              >
                <img className={classes.buttonImage} src={polygon_47} alt='' />
              </button>
            </item>
          </Grid>

          <Grid item className={classes.employeeItemEngagement}>
            <item className={classes.employeeType}>
              <p>Engagement Activities</p>
            </item>
            <Tooltip
              TransitionComponent={Zoom}
              placement='left'
              title={'Coming Soon'}
              arrow
            >
              <item className={classes.countItem}>
                <button className={classes.engagementButton}>
                  <img
                    className={classes.engagementButtonImage}
                    src={polygon_47}
                    alt=''
                  />
                </button>
              </item>
            </Tooltip>
            <item></item>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default EmployeeType

const useStyles = makeStyles({
  root: {
    width: '26vw',
    overflowY: 'hidden',
    overflowX: 'hidden',
    position: 'relative',
    borderRadius: '10px',
  },
  employeeCount: {
    fontWeight: '600px',
    color: '#B92250',
    fontSize: '30px',
    marginTop: '0px',
  },
  circularLoader: {
    margin: 'auto',
  },
  searchBarContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  employeeType: {
    width: '120px',
    height: '36px',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '17.58px',
    fontWeight: '500',
    margin: '15px',
  },
  employeeItem: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: COLORS.WHITE,
    width: '149px',
    height: '130px',
    margin: '10px',
    borderRadius: '10px',
    // padding: "40px",
    border: '1px solid rgba(0,0,0,.125)',
    boxShadow: '4px 7px 15px rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    position: 'relative',
    '@media (min-width:1600px)': { width: '130px' },
  },
  employeeItemEngagement: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: COLORS.DARK_PINK,
    color: COLORS.WHITE,
    width: '150px',
    height: '130px',
    margin: '10px',
    borderRadius: '10px',
    padding: '10px',
    boxShadow: '4px 7px 15px rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    '@media (min-width:1600px)': { width: '130px' },
  },
  arrowDownButton: {
    border: 'none',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    backgroundColor: COLORS.WHITE,
    marginBottom: '20px',
  },
  buttonImage: {
    width: '13px',
    height: '13px',
  },
  countItem: {
    display: 'flex',
    alignItems: 'center',
  },
  engagementButton: {
    border: 'none',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    marginTop: '10px',
    backgroundColor: COLORS.WHITE,
    borderRadius: '50%',
  },
  engagementButtonImage: {
    width: '13px',
    height: '13px',
    transform: 'rotate(-90deg)',
    padding: '5px',
  },
  searchBar: {
    border: '1px solid #112F3C',
    // size: "small",
    borderRadius: '9px',
    width: '80%',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    height: '25px',
    fontSize: '10px !important',
    boxShadow: 'none !important',
    // marginLeft: "1%",
    // '& .MuiSvgIcon-root': { width: '10px' },
    // '& .MuiIconButton-root': { padding: '0px !important' },
    '& .MuiTypography-root': {
      fontSize: '6px !important',
    },
    '& .ForwardRef-root-99': {
      justifyContent: 'start !important',
    },
    '& .ForwardRef-searchContainer-105': {
      width: '83%',
      margin: 'auto 0px auto 10px',
    },
  },
  gridContainer: {
    flexWrap: 'wrap',
    columns: '2 auto !important',
    padding: '10px 0px 10px 10px !important',
  },
  info: {
    position: 'absolute',
    right: '5px',
    top: '5px',
  },
  infoIcon: {
    color: '#c5b5b5',
    padding: '0p !important',
  },
  typographyStyles: {
    padding: '16px',
    height: '200px',
  },
})
